import { MAX_GROUP_DEPTH } from '../types/moduleGroup.js';
import { normalizeTag } from './moduleTags.js';
import { runsOn } from './modulePlatforms.js';
/**
 * Firestore update converts `undefined` → `null` when clearing optionals.
 * Tree indexes use `Map` keys, where `null !== undefined`, so treat both as root.
 */
function optionalRefId(id) {
    return id ?? undefined;
}
// ─── Cycle / depth guard (exported for 0149 dialog validation) ────────────────
/**
 * Rejects a `parentGroupId` that would create a cycle or exceed `MAX_GROUP_DEPTH`.
 * Pure — walks the existing `groups` chain upward from `parentGroupId`.
 */
export function assertNoGroupCycle(groups, id, parentGroupId) {
    if (!parentGroupId)
        return;
    if (parentGroupId === id) {
        throw new Error('Module group cannot be its own parent');
    }
    const byId = new Map(groups.map((g) => [g.id, g]));
    let current = parentGroupId;
    let depth = 1; // id would sit one level under parent
    const seen = new Set();
    while (current) {
        if (current === id) {
            throw new Error('Module group parent would create a cycle');
        }
        if (seen.has(current)) {
            throw new Error('Module group parent chain already contains a cycle');
        }
        seen.add(current);
        if (depth > MAX_GROUP_DEPTH) {
            throw new Error(`Module group depth exceeds maximum of ${MAX_GROUP_DEPTH}`);
        }
        const parent = byId.get(current);
        current = parent?.parentGroupId;
        depth += 1;
    }
}
/** Expand plan moduleIds ∪ tag matches over a catalog. */
export function expandPlan(plan, catalog) {
    const ids = new Set();
    for (const id of plan.moduleIds ?? []) {
        if (typeof id === 'string' && id)
            ids.add(id);
    }
    const planTags = new Set((plan.tags ?? []).map((t) => normalizeTag(t)).filter(Boolean));
    if (planTags.size > 0) {
        for (const mod of catalog) {
            const modTags = mod.tags ?? [];
            if (modTags.some((t) => planTags.has(normalizeTag(t)))) {
                ids.add(mod.id);
            }
        }
    }
    return [...ids].sort((a, b) => a.localeCompare(b));
}
/**
 * Spec §4.1 — what the org is entitled to use.
 * `plans` must already be filtered to the org's planIds and active plans.
 */
export function entitledModuleIds(plans, catalog, existing) {
    const fromPlans = new Set();
    for (const plan of plans) {
        for (const id of expandPlan(plan, catalog)) {
            fromPlans.add(id);
        }
    }
    const existingByBase = new Map();
    for (const om of existing) {
        const list = existingByBase.get(om.moduleId) ?? [];
        list.push(om);
        existingByBase.set(om.moduleId, list);
    }
    const entitled = new Set(fromPlans);
    for (const om of existing) {
        entitled.add(om.moduleId);
    }
    const missing = [];
    for (const id of fromPlans) {
        if (!existingByBase.has(id))
            missing.push(id);
    }
    missing.sort((a, b) => a.localeCompare(b));
    const orphans = [];
    for (const om of existing) {
        if (!fromPlans.has(om.moduleId))
            orphans.push(om);
    }
    return { entitled, missing, orphans };
}
// ─── §4.3 Permission ──────────────────────────────────────────────────────────
/**
 * Spec §4.3 — effective OrganizationModule ids the profile may see.
 * Deny always wins, regardless of grant order (spec §7 decision 8).
 */
export function resolveGrants(grants, groups, orgModules) {
    const childrenByParent = new Map();
    for (const g of groups) {
        const key = optionalRefId(g.parentGroupId);
        const list = childrenByParent.get(key) ?? [];
        list.push(g);
        childrenByParent.set(key, list);
    }
    const leavesByGroup = new Map();
    for (const om of orgModules) {
        const groupId = optionalRefId(om.groupId);
        if (!groupId)
            continue;
        const list = leavesByGroup.get(groupId) ?? [];
        list.push(om.id);
        leavesByGroup.set(groupId, list);
    }
    function collectLeaves(groupId, into, seen) {
        if (seen.has(groupId))
            return;
        seen.add(groupId);
        for (const leafId of leavesByGroup.get(groupId) ?? []) {
            into.add(leafId);
        }
        for (const child of childrenByParent.get(groupId) ?? []) {
            collectLeaves(child.id, into, seen);
        }
    }
    const granted = new Set();
    const denied = new Set();
    for (const grant of grants) {
        if (grant.type === 'group') {
            collectLeaves(grant.id, granted, new Set());
        }
        else if (grant.deny === true) {
            denied.add(grant.id);
        }
        else {
            granted.add(grant.id);
        }
    }
    for (const id of denied) {
        granted.delete(id);
    }
    return granted;
}
function displayName(om, base) {
    return om.name ?? base?.name ?? om.moduleId;
}
function sortKey(order, name) {
    return [order ?? Number.POSITIVE_INFINITY, name.toLocaleLowerCase()];
}
function compareNodes(a, b) {
    const [ao, an] = sortKey(a.order, a.name);
    const [bo, bn] = sortKey(b.order, b.name);
    if (ao !== bo)
        return ao - bo;
    return an.localeCompare(bn);
}
/**
 * Spec §4.2 — menu tree from 3 reads. Group appears iff it has ≥1 granted leaf
 * that also runs on `platform` (recursively). `routeModuleId` is always
 * `OrganizationModule.id`.
 *
 * `platform` is required (no silent default) so the mobile app cannot inherit
 * the console menu by forgetting the argument (request 0151).
 */
export function buildModuleTree(groups, orgModules, catalog, grantedOrgModuleIds, platform) {
    const catalogById = new Map(catalog.map((m) => [m.id, m]));
    const groupsByParent = new Map();
    for (const g of groups) {
        const key = optionalRefId(g.parentGroupId);
        const list = groupsByParent.get(key) ?? [];
        list.push(g);
        groupsByParent.set(key, list);
    }
    const leavesByGroup = new Map();
    for (const om of orgModules) {
        if (!grantedOrgModuleIds.has(om.id))
            continue;
        const base = catalogById.get(om.moduleId);
        if (!base || !runsOn(base, platform))
            continue;
        const key = optionalRefId(om.groupId);
        const list = leavesByGroup.get(key) ?? [];
        list.push(om);
        leavesByGroup.set(key, list);
    }
    function buildGroup(group) {
        const childGroups = groupsByParent.get(group.id) ?? [];
        const childLeaves = leavesByGroup.get(group.id) ?? [];
        const children = [];
        for (const child of childGroups) {
            const node = buildGroup(child);
            if (node)
                children.push(node);
        }
        for (const om of childLeaves) {
            const base = catalogById.get(om.moduleId);
            if (!base)
                continue;
            children.push({
                kind: 'leaf',
                id: om.id,
                orgModule: om,
                base,
                name: displayName(om, base),
                icon: om.icon ?? base.icon,
                description: om.description ?? base.description,
                order: om.order ?? Number.POSITIVE_INFINITY,
            });
        }
        if (children.length === 0)
            return null;
        children.sort(compareNodes);
        return {
            kind: 'group',
            id: group.id,
            group,
            name: group.name,
            icon: group.icon,
            description: group.description,
            order: group.order ?? Number.POSITIVE_INFINITY,
            children,
        };
    }
    const roots = [];
    for (const g of groupsByParent.get(undefined) ?? []) {
        const node = buildGroup(g);
        if (node)
            roots.push(node);
    }
    for (const om of leavesByGroup.get(undefined) ?? []) {
        const base = catalogById.get(om.moduleId);
        if (!base)
            continue;
        roots.push({
            kind: 'leaf',
            id: om.id,
            orgModule: om,
            base,
            name: displayName(om, base),
            icon: om.icon ?? base.icon,
            description: om.description ?? base.description,
            order: om.order ?? Number.POSITIVE_INFINITY,
        });
    }
    roots.sort(compareNodes);
    return roots;
}
/** Flatten a tree into (node, parentMid) pairs for shell instance mapping. */
export function flattenModuleTree(roots) {
    const out = [];
    function walk(nodes, parentMid) {
        for (const node of nodes) {
            out.push({ node, parentMid });
            if (node.kind === 'group')
                walk(node.children, node.id);
        }
    }
    walk(roots, undefined);
    return out;
}
/** Drop group nodes, keep every leaf at root, in tree order (group order → module order). */
export function flattenToLeaves(roots) {
    const out = [];
    function walk(nodes) {
        for (const node of nodes) {
            if (node.kind === 'leaf') {
                out.push(node);
            }
            else {
                walk(node.children);
            }
        }
    }
    walk(roots);
    return out;
}
//# sourceMappingURL=moduleResolution.js.map